/**
 * Pure state machine for the card stack + progress bar + demographics screen
 * (docs/ARCHITECTURE.md §10 Phase 3). No DOM access — kept testable in
 * isolation; src/main.ts wires this to the page.
 */

import type { Demographics, PublicDeckItem, RatingPayload, ReferrerClass, SessionState, SupportValue } from "./types.ts";
import { DEMOGRAPHIC_FIELDS } from "./types.ts";
import { CARDS_PER_SESSION, RATE_MORE_BATCH } from "./config.ts";

/** Fisher-Yates using an injectable RNG so item order is randomized per session
 * without biasing the §6.3 "positional" quality check toward deck order. */
export function shuffleDeck<T>(deck: T[], random: () => number = Math.random): T[] {
  const result = [...deck];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** The session holds the whole shuffled deck; `cap` is how many cards the
 * visitor has agreed to rate so far (CARDS_PER_SESSION at first, raised by
 * RATE_MORE_BATCH each time they choose to rate more). */
export function createSession(deck: PublicDeckItem[], opts: { now: number; random?: () => number } = { now: Date.now() }): SessionState {
  const demographics = {} as Demographics;
  for (const field of DEMOGRAPHIC_FIELDS) demographics[field] = null;

  return {
    deck: shuffleDeck(deck, opts.random),
    cap: Math.min(CARDS_PER_SESSION, deck.length),
    phase: "intro",
    currentIndex: 0,
    ratings: [],
    cardShownAt: opts.now,
    interactionEvents: 0,
    sessionStartedAt: opts.now,
    demographics,
    error: null,
  };
}

/** Intro → rating transition. Resets cardShownAt to `now` so time spent
 * reading the intro screen doesn't get counted as dwell time on card 1;
 * sessionStartedAt is left alone so total_ms still reflects the full funnel. */
export function startRating(state: SessionState, now: number): SessionState {
  return { ...state, phase: "rating", cardShownAt: now };
}

export function currentCard(state: SessionState): PublicDeckItem | undefined {
  return state.deck[state.currentIndex];
}

export function progress(state: SessionState): { current: number; total: number } {
  return { current: state.ratings.length, total: state.cap };
}

export function recordInteraction(state: SessionState): SessionState {
  return { ...state, interactionEvents: state.interactionEvents + 1 };
}

export function rateCard(state: SessionState, value: SupportValue, now: number): SessionState {
  const card = currentCard(state);
  if (!card) return state;

  const rating: RatingPayload = {
    p: card.policy_id,
    v: value,
    d: Math.max(0, now - state.cardShownAt),
    i: state.currentIndex,
  };

  const nextIndex = state.currentIndex + 1;
  const atCap = nextIndex >= state.cap;
  const deckExhausted = nextIndex >= state.deck.length;

  return {
    ...state,
    ratings: [...state.ratings, rating],
    currentIndex: nextIndex,
    cardShownAt: now,
    interactionEvents: state.interactionEvents + 1,
    phase: !atCap ? "rating" : deckExhausted ? "demographics" : "rate_more",
  };
}

/** Cards in the deck the visitor has not been shown yet. */
export function remainingUnseen(state: SessionState): number {
  return Math.max(0, state.deck.length - state.currentIndex);
}

/** "Rate more": raises the cap by up to RATE_MORE_BATCH (never past the end
 * of the deck) and resumes with the next unseen card. Resets cardShownAt so
 * time spent on the choice screen isn't counted as dwell on that card. */
export function rateMore(state: SessionState, now: number): SessionState {
  return {
    ...state,
    cap: Math.min(state.deck.length, state.cap + RATE_MORE_BATCH),
    phase: "rating",
    cardShownAt: now,
    interactionEvents: state.interactionEvents + 1,
  };
}

/** "See my results" on the rate-more screen: on to demographics. */
export function declineRateMore(state: SessionState): SessionState {
  return { ...state, phase: "demographics", interactionEvents: state.interactionEvents + 1 };
}

export function setDemographicField(state: SessionState, field: keyof Demographics, value: string): SessionState {
  return { ...state, demographics: { ...state.demographics, [field]: value }, interactionEvents: state.interactionEvents + 1 };
}

/** §9 [LOCKED]: every demographic question is skippable, and a skip records
 * `null` rather than an inferred value — never omits the key. */
export function skipDemographic(state: SessionState, field: keyof Demographics): SessionState {
  return { ...state, demographics: { ...state.demographics, [field]: null }, interactionEvents: state.interactionEvents + 1 };
}

export function finishDemographics(state: SessionState): SessionState {
  return { ...state, phase: "submitting" };
}

export function setError(state: SessionState, message: string): SessionState {
  return { ...state, phase: "error", error: message };
}

/** Error → demographics transition for the "Try again" retry path. Turnstile
 * tokens are single-use, so the token that just failed siteverify can never
 * succeed on a resubmit — retry must return to the demographics screen (where
 * main.ts remounts a fresh Turnstile widget), not resubmit directly.
 * `error` is left in place so the demographics screen can show why the rater
 * landed back here; demographics answers are untouched, since they were
 * never cleared in the first place. */
export function retryFromError(state: SessionState): SessionState {
  return { ...state, phase: "demographics" };
}

export interface SubmitPayload {
  id: string;
  turnstile_token: string;
  ratings: RatingPayload[];
  demographics: Demographics;
  session: { total_ms: number; interaction_events: number };
  referrer_class: ReferrerClass;
}

export function buildSubmitPayload(state: SessionState, id: string, turnstileToken: string, referrerClass: ReferrerClass, now: number = Date.now()): SubmitPayload {
  return {
    id,
    turnstile_token: turnstileToken,
    ratings: state.ratings,
    demographics: state.demographics,
    session: {
      total_ms: Math.max(0, now - state.sessionStartedAt),
      interaction_events: state.interactionEvents,
    },
    referrer_class: referrerClass,
  };
}
